import { useUserControllerAttribute } from "@/generated/api/api";
import { useSession } from "@/utils/session.ctx";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useFocusEffect, useGlobalSearchParams, useRouter } from "expo-router";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Buffer } from "buffer";

const CTX = createContext<{
  attributionId?: string;
}>(null!);

export function AttributionProvider(props: PropsWithChildren) {
  const { gclid: gclidraw, _gl: glraw } = useGlobalSearchParams<{
    gclid?: string;
    _gl?: string;
  }>();
  const router = useRouter();
  const session = useSession();
  // undefined - not initialized
  // null - loaded, no gclid
  // string - loaded gclid defined
  const [attributionId, setAttributionId] = useState<
    undefined | null | string
  >();

  const { mutate } = useUserControllerAttribute({
    mutation: {
      meta: {
        noSystemError: true,
      },
    },
  });

  const gclid = useMemo(
    () => (gclidraw ? gclidraw : glraw ? extractGclidFromGl(glraw) : undefined),
    [gclidraw, glraw]
  );

  useEffect(() => {
    if (gclid && gclid !== "null" && gclid !== "undefined") {
      setAttributionId(gclid);
      AsyncStorage.setItem("attributionId", gclid).then(() => {});
    }
  }, [gclid]);

  useFocusEffect(
    useCallback(() => {
      if (attributionId && gclid && gclid !== "null" && gclid !== "undefined") {
        router.setParams({ gclid: undefined });
      }
    }, [gclid, attributionId])
  );

  useEffect(() => {
    if (attributionId === undefined) {
      AsyncStorage.getItem("attributionId").then((v) => {
        setAttributionId(v || null);
      });
    }
  }, [attributionId]);

  useEffect(() => {
    if (
      session.isInitialized &&
      session.isAuthenticated &&
      typeof attributionId === "string"
    ) {
      mutate(
        {
          data: {
            attributionId,
          },
        },
        {
          onSuccess() {
            AsyncStorage.removeItem("attributionId");
          },
        }
      );
    }
  }, [attributionId, session.isInitialized, session.isAuthenticated]);

  return (
    <CTX.Provider
      value={{
        attributionId: attributionId || undefined,
      }}
    >
      {props.children}
    </CTX.Provider>
  );
}

export function useAttribution() {
  return useContext(CTX);
}

function extractGclidFromGl(glParam: string) {
  if (glParam) {
    const parts = glParam.split("*");
    let encodedGclAwValue = null;

    for (let i = 0; i < parts.length; i++) {
      if (parts[i] === "_gcl_aw" && parts[i + 1]) {
        encodedGclAwValue = parts[i + 1];
        break;
      }
    }

    if (encodedGclAwValue) {
      try {
        // ИСПРАВЛЕНИЕ: Заменяем конечную точку на '=' для корректного Base64-декодирования
        // Это предполагает, что Base64 строка должна быть дополнена '='
        // и что точка в конце является нестандартным маркером, который надо исправить.
        if (encodedGclAwValue.endsWith(".")) {
          encodedGclAwValue = encodedGclAwValue.slice(0, -1) + "=";
        }
        // Также можно попробовать дополнить, если не хватает до кратности 4, но начнем с точки
        while (encodedGclAwValue.length % 4 !== 0) {
          encodedGclAwValue += "=";
        }

        const decodedGclAwValue = Buffer.from(
          encodedGclAwValue,
          "base64"
        ).toString("utf-8");
        console.log(
          "Decoded _gcl_aw value (after correction):",
          decodedGclAwValue
        );

        const gclidParts = decodedGclAwValue.split(".");
        if (gclidParts.length > 2) {
          return gclidParts[gclidParts.length - 1];
        }
      } catch (e) {
        console.error(
          "Error decoding _gcl_aw value from _gl parameter (after correction):",
          e
        );
      }
    }
  }
}
